'use client'

import React, { useState } from 'react'
import type { FieldError as HookFieldError } from 'react-hook-form'

import { cn } from '@/lib/utils'
import FieldError from '@/components/livecontact/FieldError'
import LcIcon from '@/components/livecontact/icons'
import {
  CHIP_CLASS,
  CHIP_REMOVE_CLASS,
  FOCUS_RING,
  INPUT_ERROR_CLASS,
  LABEL_CLASS,
  NOTE_CLASS,
} from '@/components/livecontact/fieldStyles'

type Props = {
  value: string[]
  onChange: (items: string[]) => void

  label?: string
  placeholder?: string
  disabled?: boolean
  max?: number
  error?: HookFieldError
  description?: string
  className?: string
  note?: string

  emptyPlaceholder?: string

  /** Validate single item */
  validate?: (value: string) => boolean

  /** Normalize input value */
  transform?: (value: string) => string

  /** Custom duplicate check */
  equals?: (a: string, b: string) => boolean

  /** Key triggers */
  separators?: string[]
}

/** Chip list + bare input inside one field surface; Enter / comma adds, Backspace removes. */
const MultiValueField = ({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  max,
  error,
  note,
  className,
  validate,
  transform = (v) => v.trim(),
  equals = (a: string, b: string) => a === b,
  separators = ['Enter', ','],
  emptyPlaceholder,
}: Props) => {
  const [input, setInput] = useState('')

  const addItem = (raw: string) => {
    const normalized = transform(raw)
    if (!normalized) return
    if (validate && !validate(normalized)) return
    if (value.some((item) => equals(item, normalized))) return
    if (max && value.length >= max) return

    onChange([...value, normalized])
    setInput('')
  }

  const removeItem = (item: string) => onChange(value.filter((i) => !equals(i, item)))

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (separators.includes(e.key)) {
      e.preventDefault()
      addItem(input)
    }
    if (e.key === 'Backspace' && !input && value.length) {
      onChange(value.slice(0, -1))
    }
  }

  return (
    <div className={cn('w-full', className)}>
      {label && <label className={LABEL_CLASS}>{label}</label>}

      <div
        className={cn(
          'flex min-h-[96px] w-full flex-col gap-2 rounded-[10px] bg-surface p-2 shadow-input transition-shadow duration-[160ms] ease-out-strong',
          `focus-within:shadow-[${FOCUS_RING}]`,
          disabled && 'opacity-60',
          error && INPUT_ERROR_CLASS
        )}
        onClick={(e) =>
          (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus()
        }
      >
        {value.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {value.map((item) => (
              <span key={item} className={cn(CHIP_CLASS, 'animate-lc-pop')}>
                {item}
                <button
                  type="button"
                  onClick={() => removeItem(item)}
                  disabled={disabled}
                  aria-label={`Remove ${item}`}
                  className={CHIP_REMOVE_CLASS}
                >
                  <LcIcon name="close" size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        <input
          type="text"
          disabled={disabled || (max !== undefined && value.length >= max)}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => input.trim() && addItem(input)}
          className="h-7 w-full bg-transparent px-1 text-[14px] leading-[1.4] tracking-[-0.2px] text-ink outline-none placeholder:text-ink-4 disabled:cursor-not-allowed"
          placeholder={value.length === 0 ? (emptyPlaceholder ?? placeholder) : placeholder}
          aria-invalid={!!error}
        />
      </div>

      {(note || max) && (
        <p className={cn(NOTE_CLASS, 'flex items-baseline justify-between gap-3')}>
          <span>{note}</span>
          {max && (
            <span className={cn('shrink-0 tabular-nums', value.length >= max && 'text-ink-3')}>
              {value.length}/{max}
            </span>
          )}
        </p>
      )}
      <FieldError message={error?.message} />
    </div>
  )
}

export default MultiValueField
